import { useEffect, useState } from "react";
import { addDays, format, startOfWeek, isSameDay } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Truck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

function eventsFor(services, v, day) {
  const out = [];
  services.forEach((s) => {
    const list = v.is_ambulance ? s.ambulances : s.required_vehicles;
    const hit = v.pending
      ? ((s.required_ambulances || 0) > (s.ambulances || []).length ? {} : null)
      : (list || []).find((a) => a.id === v.id);
    if (!hit) return;
    (s.dates?.length ? s.dates : [s.date_time]).forEach((d) => {
      const dt = new Date(d);
      if (isSameDay(dt, day)) out.push({ svc: s, start: format(dt, "HH:mm"), end: s.end_times?.[d] || "", at: dt, driver: hit.driver_name });
    });
  });
  return out.sort((a, b) => a.at - b.at);
}

export function VehicleWeekView({ services, onSelect }) {
  const [vehicles, setVehicles] = useState(null);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const ambs = (vehicles || []).filter((v) => v.is_ambulance);
  const pendingRow = { id: "da-assegnare", name: "Ambulanza da assegnare", plate: "scegli la targa nel servizio", is_ambulance: true, pending: true };
  const others = (vehicles || []).filter((v) => !v.is_ambulance);
  const today = new Date();

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => setVehicles([]));
  }, []);

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="vehicle-week-view">
      <div className="flex items-center justify-between mb-4">
        <Button size="icon" variant="ghost" data-testid="vehicles-prev-week" onClick={() => setWeekStart((w) => addDays(w, -7))}><ChevronLeft className="h-5 w-5" /></Button>
        <div className="text-center">
          <p className="font-display font-semibold" data-testid="vehicles-week-label">
            {format(days[0], "d MMM", { locale: it })} – {format(days[6], "d MMM yyyy", { locale: it })}
          </p>
          <button type="button" data-testid="vehicles-this-week" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="text-xs text-[#D32F2F] hover:underline">Questa settimana</button>
        </div>
        <Button size="icon" variant="ghost" data-testid="vehicles-next-week" onClick={() => setWeekStart((w) => addDays(w, 7))}><ChevronRight className="h-5 w-5" /></Button>
      </div>
      {vehicles === null ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Caricamento…</p>
      ) : vehicles.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Nessun mezzo nell'Autoparco.</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[860px] grid" style={{ gridTemplateColumns: "170px repeat(7, minmax(0, 1fr))" }}>
            <div />
            {days.map((d) => (
              <div key={d.toISOString()} className={`px-2 pb-2 text-xs font-semibold uppercase text-center ${isSameDay(d, today) ? "text-[#D32F2F]" : "text-muted-foreground"}`}>
                {format(d, "EEE d", { locale: it })}
              </div>
            ))}
            <GroupHeader label="Ambulanze" empty={ambs.length === 0 ? "Nessuna: spunta “È un'ambulanza” in Admin → Autoparco" : ""} />
            {ambs.map((v) => (
              <VehicleRow key={v.id} v={v} days={days} services={services} onSelect={onSelect} />
            ))}
            <VehicleRow v={pendingRow} days={days} services={services} onSelect={onSelect} />
            {others.length > 0 && <GroupHeader label="Altri mezzi" />}
            {others.map((v) => (
              <VehicleRow key={v.id} v={v} days={days} services={services} onSelect={onSelect} />
            ))}
          </div>
        </div>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground">Celle vuote = mezzo libero. "Ambulanza da assegnare" = servizi che chiedono un'ambulanza senza targa scelta: tocca l'impegno per sceglierla.</p>
    </div>
  );
}

function GroupHeader({ label, empty }) {
  return (
    <div className="col-span-8 border-t border-border pt-3 pb-1 flex items-baseline gap-2" data-testid={`vehicle-group-${label}`}>
      <span className="sticky left-0 text-xs font-bold uppercase tracking-wider text-[#D32F2F]">{label}</span>
      {empty && <span className="text-[11px] text-muted-foreground">{empty}</span>}
    </div>
  );
}

function VehicleRow({ v, days, services, onSelect }) {
  return (
    <>
      <div className="border-t border-border py-2 pr-2 sticky left-0 z-10 bg-card" data-testid={`vehicle-row-${v.id}`}>
        <p className="text-sm font-semibold flex items-center gap-1.5"><Truck className="h-4 w-4 text-[#D32F2F]" />{v.name}</p>
        {v.plate && <p className="font-mono text-xs text-muted-foreground">{v.plate}</p>}
      </div>
      {days.map((d) => {
        const evs = eventsFor(services, v, d);
        return (
          <div key={d.toISOString()} className={`border-t border-l border-border p-1 min-h-[64px] space-y-1 ${evs.length || v.pending ? "" : "bg-emerald-50/40 dark:bg-emerald-950/10"}`}
            data-testid={`vehicle-cell-${v.id}-${format(d, "yyyy-MM-dd")}`}>
            {evs.map((e, i) => (
              <button key={i} type="button" onClick={() => onSelect(e.svc)} data-testid={`vehicle-event-${e.svc.id}`}
                className={`w-full text-left rounded-md px-1.5 py-1 text-[11px] leading-tight border transition-colors hover:brightness-95 ${v.pending ? "bg-slate-100 border-dashed border-slate-400 text-slate-700" : evs.length > 1 ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-[#D32F2F]/10 border-[#D32F2F]/30 text-[#7f1d1d]"}`}>
                <span className="font-mono font-semibold">{e.start}{e.end ? `–${e.end}` : ""}</span>
                <span className="block truncate">{e.svc.event_name}</span>
                {!v.is_ambulance && <span className="block truncate opacity-80">{e.driver ? `Autista: ${e.driver}` : "Senza autista"}</span>}
              </button>
            ))}
          </div>
        );
      })}
    </>
  );
}
